import {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";
import { useLocation, useParams } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import {
  Box, Typography, IconButton, Chip, Tooltip, Alert,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import SmartToyRoundedIcon from "@mui/icons-material/SmartToyRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import { useTenant } from "../context/TenantContext";
import { useDashboardFilters } from "../context/DashboardFiltersContext";
import {
  streamAssistantMessage, sendAssistantMessage, AssistantResponse,
} from "../lib/sophiaApi";

interface Props {
  onClose?: () => void;
  variant: "inline" | "drawer";
}

interface Msg {
  role: "user" | "assistant" | "system";
  content: string;
  timestamp: string;
}

const DEFAULT_LOADING_MESSAGE = "Pensando no contexto visual...";
const MAX_QUEUE = 3;

const EXAMPLES_BY_ROUTE: Record<string, string[]> = {
  "/dashboards": [
    "Resuma o desempenho deste mês",
    "Compare com o mês anterior",
    "Onde está a maior queda?",
  ],
  "/relatorios": [
    "Quais relatórios falharam ontem?",
    "Reenvia o último de compras",
  ],
  "/integracoes": [
    "Alguma sync com erro?",
    "Última execução do sync de vendas",
  ],
  "/": [
    "Como está o mês?",
    "Quais integrações estão paradas?",
  ],
};

function displayText(content: string, role: "user" | "assistant" | "system"): string {
  // Sanitize simple markdown from LLM responses so the raw *bold* / **bold** / `code`
  // markers don't show up as plain text.
  if (role === "user") return content;
  let out = String(content || "");
  out = out.replace(/\*\*(.+?)\*\*/g, "$1");
  out = out.replace(/(?<![*])\*([^*\n]+)\*(?![*])/g, "$1");
  out = out.replace(/`([^`]+)`/g, "$1");
  return out;
}

export function SophiaPanel({ onClose }: Props) {
  const auth = useAuth();
  const location = useLocation();
  const params = useParams();
  const { tenant } = useTenant();
  const dashFilters = useDashboardFilters();

  const [chat, setChat] = useState<Msg[]>([
    {
      role: "assistant",
      content: `Oi! Sou a Sophia. ${tenant ? `Você está em ${tenant}.` : "Escolha um tenant no topo pra começar."} Pode me perguntar sobre vendas, filtrar dashboards, pedir análises ou disparar ações.`,
      timestamp: new Date().toISOString(),
    },
  ]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(DEFAULT_LOADING_MESSAGE);
  // Texto que a Sophia esta escrevendo agora, montado a partir dos frames
  // `delta`. Enquanto ele existe, ele substitui a frase de "pensando".
  const [streamingText, setStreamingText] = useState("");
  const [queue, setQueue] = useState<string[]>([]);

  const abortRef = useRef<AbortController | null>(null);
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const stateRef = useRef(dashFilters.filters);
  useEffect(() => { stateRef.current = dashFilters.filters; }, [dashFilters.filters]);

  // Build auth headers each request so token refresh takes effect.
  const getAuthHeaders = useCallback((): Record<string, string> => {
    const h: Record<string, string> = {};
    const token = auth.user?.access_token;
    if (token) h["Authorization"] = `Bearer ${token}`;
    if (tenant) h["X-Tenant"] = tenant;
    return h;
  }, [auth.user?.access_token, tenant]);

  const dashboardId = params.id && location.pathname.startsWith("/dashboards/")
    ? String(params.id) : undefined;

  const examples = useMemo(() => {
    if (dashboardId) return EXAMPLES_BY_ROUTE["/dashboards"] ?? [];
    return EXAMPLES_BY_ROUTE[location.pathname] ?? EXAMPLES_BY_ROUTE["/"] ?? [];
  }, [dashboardId, location.pathname]);

  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [chat, loading, loadingMessage, streamingText]);

  const addChat = useCallback((role: Msg["role"], content: string) => {
    setChat((prev) => [...prev, { role, content, timestamp: new Date().toISOString() }]);
  }, []);

  // Depende de `applyPatch`, nao do objeto inteiro do contexto: `dashFilters`
  // troca de identidade a cada mudanca de filtro, e arrastava junto
  // applyAssistantResult -> runTurn -> o efeito da fila, que roda a cada
  // render. A funcao e estavel; o objeto que a carrega nao e.
  const aplicarFiltro = dashFilters.applyPatch;
  const applyAssistantResult = useCallback((result: AssistantResponse) => {
    const patch = result.state_patch || {};
    if (dashboardId && patch && typeof patch === "object") {
      aplicarFiltro(patch as any);
    }
    const msg = displayText(result.message || "Feito.", "assistant");
    addChat("assistant", msg);
  }, [addChat, dashboardId, aplicarFiltro]);

  const runTurn = useCallback(async (message: string) => {
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setLoadingMessage(DEFAULT_LOADING_MESSAGE);
    setStreamingText("");
    let settled = false;
    const finish = () => { settled = true; };

    const streamOpts = {
      dashboardId,
      state: dashboardId ? {
        page: stateRef.current.page,
        start_month: stateRef.current.start_month,
        end_month: stateRef.current.end_month,
        filters: stateRef.current.filters,
        selections: stateRef.current.selections,
        focus: stateRef.current.focus,
        theme: stateRef.current.theme,
      } : undefined,
      getAuthHeaders,
    };

    try {
      await streamAssistantMessage(message, streamOpts, {
        signal: controller.signal,
        onStart: () => setLoading(true),
        onProgress: (phase) => setLoadingMessage(phase),
        onDelta: (text) => setStreamingText((atual) => atual + text),
        onDone: (result) => { finish(); applyAssistantResult(result); },
        onError: (msg) => { finish(); addChat("assistant", msg); },
      });

      // Fallback if the stream ended without a resolved event (network hiccup)
      if (!settled && !controller.signal.aborted) {
        finish();
        const result = await sendAssistantMessage(message, {
          ...streamOpts,
          signal: controller.signal,
        });
        applyAssistantResult(result);
      }
    } catch (error) {
      if (controller.signal.aborted) return;
      if (!settled) {
        finish();
        try {
          const result = await sendAssistantMessage(message, {
            ...streamOpts,
            signal: controller.signal,
          });
          applyAssistantResult(result);
        } catch (fallbackError: any) {
          addChat("assistant", `Não consegui: ${fallbackError?.message || "erro desconhecido"}`);
        }
      }
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      if (!controller.signal.aborted) {
        setLoading(false);
        setLoadingMessage(DEFAULT_LOADING_MESSAGE);
        setStreamingText("");
      }
    }
  }, [addChat, applyAssistantResult, dashboardId, getAuthHeaders]);

  // Drain FIFO queue
  useEffect(() => {
    if (loading || !queue.length) return;
    const [next, ...rest] = queue;
    setQueue(rest);
    void runTurn(next);
  }, [loading, queue, runTurn]);

  const enqueueMessage = useCallback((message: string) => {
    const value = message.trim();
    if (!value) return;
    addChat("user", value);
    if (!loading && !abortRef.current) {
      void runTurn(value);
      return;
    }
    setQueue((current) => (current.length >= MAX_QUEUE ? current : [...current, value]));
  }, [addChat, loading, runTurn]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.trim() || loading) return;
    enqueueMessage(draft);
    setDraft("");
  };

  const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as any);
    }
  };

  const handleStop = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setLoading(false);
    setLoadingMessage(DEFAULT_LOADING_MESSAGE);
    addChat("system", "Resposta interrompida.");
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "grid",
        gridTemplateRows: "auto auto minmax(0, 1fr) auto",
        gap: 1,
        bgcolor: "background.paper",
        overflow: "hidden",
      }}
      data-testid="sophia-panel"
    >
      {/* Header */}
      <Box sx={{
        p: 1.5,
        background: "linear-gradient(135deg, #021a2c 0%, #2c4055 100%)",
        color: "common.white",
        display: "flex", alignItems: "center", gap: 1.25,
      }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: 2, bgcolor: "secondary.main",
          display: "grid", placeItems: "center", color: "primary.dark",
        }}>
          <SmartToyRoundedIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{
            color: "common.white", lineHeight: 1.1,
            fontFamily: "'Oswald',sans-serif", letterSpacing: "0.06em",
          }}>
            SOPHIA
          </Typography>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)" }}>
            {tenant ? `assistente · ${tenant}` : "assistente"}
          </Typography>
        </Box>
        {onClose && (
          <IconButton
            onClick={onClose}
            aria-label="Fechar Sophia"
            sx={{ color: "common.white", width: 44, height: 44 }}
          >
            <CloseRoundedIcon />
          </IconButton>
        )}
      </Box>

      {/* Examples row (fills draft, doesn't send) */}
      <Box sx={{
        px: 1.5, py: 1, display: "flex", gap: 0.75,
        overflowX: "auto", bgcolor: "background.default",
        borderBottom: 1, borderColor: "divider",
      }}>
        {examples.map((ex) => (
          <Tooltip key={ex} title="Preencher no campo (não envia)">
            <Chip
              icon={<AutoAwesomeRoundedIcon sx={{ fontSize: "16px !important" }} />}
              label={ex}
              onClick={() => setDraft(ex)}
              sx={{
                bgcolor: "secondary.light", color: "primary.dark",
                fontWeight: 500, whiteSpace: "nowrap", flexShrink: 0,
                height: 36, fontSize: 13,
              }}
            />
          </Tooltip>
        ))}
      </Box>

      {/* Messages — this is the ONLY scrolling region */}
      <Box
        ref={messagesRef}
        sx={{
          overflowY: "auto", overflowX: "hidden",
          px: 1.5, py: 1.5,
          display: "flex", flexDirection: "column", gap: 1,
          bgcolor: "background.default",
        }}
      >
        {chat.map((m, i) => {
          if (m.role === "system") {
            return (
              <Alert key={i} severity="info" sx={{ fontSize: 12, py: 0.5, borderRadius: 2 }}>
                {m.content}
              </Alert>
            );
          }
          const isUser = m.role === "user";
          return (
            <Box
              key={i}
              sx={{
                alignSelf: isUser ? "flex-end" : "flex-start",
                maxWidth: "88%",
                px: 1.25, py: 1,
                borderRadius: 2,
                bgcolor: isUser ? "primary.main" : "background.paper",
                color: isUser ? "common.white" : "text.primary",
                border: isUser ? "none" : "1px solid",
                borderColor: isUser ? "transparent" : "divider",
                fontSize: 13, lineHeight: 1.5,
                borderBottomRightRadius: isUser ? 4 : 12,
                borderBottomLeftRadius: !isUser ? 4 : 12,
                whiteSpace: "pre-wrap", wordBreak: "break-word",
              }}
            >
              <Typography variant="caption" sx={{
                display: "block", fontWeight: 600, mb: 0.25,
                color: isUser ? "rgba(255,255,255,0.85)" : "primary.dark",
              }}>
                {isUser ? "Você" : "Sophia"}
              </Typography>
              {m.content}
            </Box>
          );
        })}
        {loading && (
          <Box
            sx={{
              alignSelf: "flex-start", maxWidth: "88%",
              px: 1.25, py: 1, borderRadius: 2,
              bgcolor: "background.paper",
              border: "1px solid", borderColor: "divider",
              borderBottomLeftRadius: 4,
              fontSize: 13, color: "text.secondary",
              display: "flex", alignItems: "center", gap: 1,
            }}
            aria-live="polite"
          >
            <Box sx={{
              width: 8, height: 8, borderRadius: 4, bgcolor: "secondary.main",
              animation: "pulse 1.2s ease-in-out infinite",
              "@keyframes pulse": {
                "0%,100%": { opacity: 0.3 }, "50%": { opacity: 1 },
              },
              display: streamingText ? "none" : undefined,
            }} />
            {streamingText ? (
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", color: "text.primary" }}>
                {streamingText}
              </Typography>
            ) : (
              <Typography variant="caption" sx={{ fontStyle: "italic" }}>{loadingMessage}</Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Input form (never scrolls out — grid row auto) */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          p: 1.25, display: "flex", flexDirection: "column", gap: 0.5,
          borderTop: 1, borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        {queue.length > 0 && (
          <Typography variant="caption" sx={{ color: "text.secondary", pl: 1 }} aria-live="polite">
            {queue.length} na fila
          </Typography>
        )}
        <Box sx={{ display: "flex", gap: 0.75, alignItems: "flex-end" }}>
          <Box
            component="textarea"
            value={draft}
            onChange={(e: any) => setDraft(e.target.value)}
            onKeyDown={handleTextareaKeyDown}
            placeholder="Peça uma análise, filtro ou destaque visual."
            rows={2}
            data-testid="sophia-input"
            sx={{
              flex: 1, resize: "none",
              padding: "8px 10px",
              fontFamily: "inherit", fontSize: 13, lineHeight: 1.4,
              color: "text.primary", background: "background.default",
              border: 1, borderColor: "divider", borderRadius: 1.5,
              outline: "none",
              "&:focus": { borderColor: "primary.main" },
            }}
          />
          {loading ? (
            <Tooltip title="Parar resposta">
              <IconButton
                onClick={handleStop} type="button"
                sx={{ bgcolor: "error.main", color: "common.white", width: 44, height: 44,
                      "&:hover": { bgcolor: "error.dark" } }}
                aria-label="Parar resposta"
              >
                <StopRoundedIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Enviar (Enter)">
              <IconButton
                type="submit" disabled={!draft.trim()}
                sx={{ bgcolor: "primary.main", color: "common.white", width: 44, height: 44,
                      "&:hover": { bgcolor: "primary.dark" },
                      "&.Mui-disabled": { bgcolor: "grey.300", color: "grey.500" } }}
                aria-label="Enviar mensagem"
              >
                <SendRoundedIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
}
